import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

// Own storage key — mirrors how the theme preference persists
// ('ssp-enterprise-theme'); independent preferences live in independent keys.
const PRIVACY_STORAGE_KEY = 'ssp-enterprise-privacy';

interface PrivacyContextValue {
  hidden: boolean;
  togglePrivacy: () => void;
}

const PrivacyContext = createContext<PrivacyContextValue | undefined>(undefined);

/**
 * Replacement string for chart axis/tooltip formatters while privacy mode is
 * on. CSS blur (`privacy-sensitive`) cannot reliably reach chart-internal
 * text (canvas/tooltip portals), so chart formatters return this mask
 * instead of the real number.
 */
export const PRIVACY_MASK = '••••';

function loadInitialHidden(): boolean {
  try {
    const stored = localStorage.getItem(PRIVACY_STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as { hidden?: unknown }).hidden === 'boolean'
      ) {
        return (parsed as { hidden: boolean }).hidden;
      }
    }
  } catch {
    // Corrupt/unavailable storage — fall through to visible default
  }
  return false;
}

/**
 * Privacy mode (ported from ssp-wallet): blur all balances/portfolio totals
 * (shoulder-surfing / screen-share protection). Elements opt in with the
 * `privacy-sensitive` class; the blur is applied via a `privacy-hidden` class
 * stamped on <html>. Deliberately NOT applied to transaction/proposal amounts
 * under review — users must be able to verify what they sign.
 */
export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<boolean>(loadInitialHidden);

  useEffect(() => {
    document.documentElement.classList.toggle('privacy-hidden', hidden);
    return () => {
      document.documentElement.classList.remove('privacy-hidden');
    };
  }, [hidden]);

  const togglePrivacy = useCallback(() => {
    setHidden((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify({ hidden: next }));
      } catch (error) {
        console.error('[PRIVACY] Failed to persist privacy mode', error);
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ hidden, togglePrivacy }), [hidden, togglePrivacy]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export function usePrivacyMode(): PrivacyContextValue {
  const ctx = useContext(PrivacyContext);
  if (!ctx) {
    throw new Error('usePrivacyMode must be used within PrivacyProvider');
  }
  return ctx;
}
